import { useMemo } from 'react'
import { InfoTypeNamespace } from '@/types/info-types'
import { ProjectGrid } from '@/types/projects'
import { useGetInfoTypeNamespaces } from '@/service-library/hooks/info-type-namespaces'
import { useGetProjects } from '@/service-library/hooks/projects'
import { useRootOrganization } from '@/components/organizations/RootOrganizationProvider'

const ZERAPIX_NAMESPACE_ID = '01952090-0836-0360-3455-15c6e036f3a9'

type UseGetGroupedNamespacesOptions = {
  enabled: boolean
  projectGrid: ProjectGrid
}

function formatNamespaces({
  namespaces,
  rootOrgName,
  isGlobal = true,
  isBase = true,
}: {
  namespaces: InfoTypeNamespace[]
  rootOrgName: string
  isGlobal?: boolean
  isBase?: boolean
}) {
  const formattedNamespaces = namespaces.map((namespace) => {
    const rootNamespaceName = namespace.fullname.split('|')[1] || ''
    let group = ''
    if (isGlobal) {
      group = 'Global'
    } else {
      group = isBase ? rootOrgName : rootNamespaceName
    }

    return {
      ...namespace,
      name: isBase ? rootNamespaceName : namespace.name,
      group,
    }
  })

  if (!isGlobal) {
    return formattedNamespaces.sort((a, b) => a.group.localeCompare(b.group))
  }

  return formattedNamespaces
}

export default function useGetNamespaceOptions({
  enabled,
  projectGrid,
}: UseGetGroupedNamespacesOptions) {
  const { rootOrganization } = useRootOrganization()

  const isSubGrid = !!projectGrid.parent_project_grid_id
  const { infoTypeNamespaces: zerapixNamespaces } = useGetInfoTypeNamespaces({
    filters: {
      limit: '1000',
      parent_id: ZERAPIX_NAMESPACE_ID,
      fields__only: 'id',
    },
    enabled,
  })

  const { infoTypeNamespaces: globalBaseNamespaces } = useGetInfoTypeNamespaces(
    {
      filters: {
        limit: '1000',
        parent_id__in: zerapixNamespaces.map((n) => n.id).join(),
      },
      enabled: enabled && !!zerapixNamespaces[0],
    },
  )

  const { infoTypeNamespaces: globalTableNamespaces } =
    useGetInfoTypeNamespaces({
      filters: {
        limit: '1000',
        parent_id__in: globalBaseNamespaces.map((n) => n.id).join(),
      },
      enabled: enabled && isSubGrid && !!globalBaseNamespaces[0],
    })

  const { projects } = useGetProjects({
    filters: {
      limit: '1000',
      org_id: rootOrganization.id,
      fields__only: 'id,namespace_id',
    },
    enabled,
  })

  const { infoTypeNamespaces: projectsBaseNamespaces } =
    useGetInfoTypeNamespaces({
      filters: {
        limit: '1000',
        parent_id__in: projects.map((p) => p.namespace_id).join(),
      },
      enabled: enabled && !!projects[0],
    })

  const { infoTypeNamespaces: projectsTableNamespaces } =
    useGetInfoTypeNamespaces({
      filters: {
        limit: '1000',
        parent_id__in: projectsBaseNamespaces.map((n) => n.id).join(),
      },
      enabled: enabled && isSubGrid && !!projectsBaseNamespaces[0],
    })

  const globalNamespaces = {
    base: formatNamespaces({
      namespaces: globalBaseNamespaces,
      rootOrgName: rootOrganization.name,
    }),
    table: isSubGrid
      ? formatNamespaces({
          namespaces: globalTableNamespaces,
          rootOrgName: rootOrganization.name,
          isBase: false,
        })
      : [],
  }

  const projectNamespaces = {
    base: formatNamespaces({
      namespaces: projectsBaseNamespaces,
      rootOrgName: rootOrganization.name,
      isGlobal: false,
    }),
    table: isSubGrid
      ? formatNamespaces({
          namespaces: projectsTableNamespaces,
          rootOrgName: rootOrganization.name,
          isGlobal: false,
          isBase: false,
        })
      : [],
  }
  const allNamespaces = useMemo(() => {
    if (!isSubGrid) {
      return [...globalNamespaces.base, ...projectNamespaces.base]
    }
    return [...globalNamespaces.table, ...projectNamespaces.table]
  }, [
    globalNamespaces.base,
    globalNamespaces.table,
    isSubGrid,
    projectNamespaces.base,
    projectNamespaces.table,
  ])

  return {
    allNamespaces,
    globalNamespaces,
    projectNamespaces,
  }
}
